import { useEffect, useState } from "react";
import { InfoIcon } from "lucide-react";

import { EM_DASH, formatDateTime, formatDuration } from "~/lib/format";

/**
 * The delivery machinery's constants and the three things about it that the
 * screen has to explain rather than just display.
 *
 * Every number here mirrors the server. The retry schedule is
 * internal/worker/callback/backoff.go; the lease and sweep intervals are
 * config/config.go (CALLBACK_LEASE_TIMEOUT_SECONDS default 300,
 * CALLBACK_HOUSEKEEPING_INTERVAL_MINUTES default 60). Both are env-tunable, so
 * this file is the one place a deployment that changes them has to be reflected.
 */

/** The lease a worker holds while an HTTP call is in flight. */
export const LEASE_MS = 5 * 60 * 1000;

/** How often housekeeping releases leases from workers that died. Deliberately
 *  longer than Neon's autosuspend window. */
export const HOUSEKEEPING = "hourly";

/** 10s, 30s, 2m, 10m, 30m, 2h, 6h with ±20% jitter, clamping at the last. */
export const RETRY_SCHEDULE = "10s, 30s, 2m, 10m, 30m, 2h, then 6h";

/** A short explanatory note, in the muted style the detail screens use. */
export function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
      <InfoIcon className="mt-0.5 size-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/**
 * Why a delivery that has not been retried is not stuck.
 *
 * "Why hasn't it retried" is almost always "because it isn't due yet", and a
 * bare RFC 3339 timestamp does not answer that at a glance -- you have to know
 * what time it is now and do the subtraction. The countdown does it for you.
 */
export function NextAttemptCountdown({ at }: { at: string | undefined }) {
  const [now, setNow] = useState(() => Date.now());

  // A second is enough: this is a countdown someone glances at, not a timer.
  // The interval is skipped entirely once there is nothing to count down to,
  // which is every terminal delivery on the page.
  useEffect(() => {
    if (!at) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [at]);

  if (!at) return <span className="text-muted-foreground">{EM_DASH}</span>;

  const target = new Date(at).getTime();
  if (Number.isNaN(target)) return <span className="text-muted-foreground">{EM_DASH}</span>;

  const remaining = target - now;

  return (
    <span className="whitespace-nowrap" title={formatDateTime(at)}>
      {formatDateTime(at)}{" "}
      <span className="text-muted-foreground tabular-nums">
        {/* Overdue is a real state and worth naming: a due delivery that has not
            moved means no worker is draining the queue, which is a different
            problem from a merchant returning 502. */}
        {remaining > 0 ? `(in ${formatDuration(remaining)})` : "(due now)"}
      </span>
    </span>
  );
}

/**
 * Reports whether a lease has outlived the worker that took it.
 *
 * A row that is 'delivering' with a locked_at older than the lease means the
 * worker died mid-flight; housekeeping will return it to the queue on its next
 * hourly pass. Without this the row just sits there looking wrong.
 */
export function isStaleLease(lockedAt: string | null | undefined): boolean {
  if (!lockedAt) return false;
  const at = new Date(lockedAt).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at > LEASE_MS;
}
